import type {
  BottomTabBarProps,
  BottomTabNavigationOptions,
} from 'expo-router/js-tabs'
import type { ColorValue } from 'react-native'
import { Pressable, Text, View } from 'react-native'
import { colors } from '@/src/theme'

function getLabel(
  tabBarLabel: BottomTabNavigationOptions['tabBarLabel'],
  options: { isFocused: boolean; color: string },
) {
  if (tabBarLabel == null) {
    return null
  }
  if (typeof tabBarLabel === 'function') {
    return tabBarLabel({
      focused: options.isFocused,
      color: options.color,
      position: 'below-icon',
      children: '',
    })
  }
  return tabBarLabel
}

// Local tab bar override to avoid React Navigation web pointerEvents deprecation noise.
export function AccountTabBar({
  state,
  descriptors,
  navigation,
  insets,
}: BottomTabBarProps) {
  const focusedRoute = state.routes[state.index]
  const focusedOptions = descriptors[focusedRoute.key]?.options
  const activeTintColor: ColorValue =
    focusedOptions?.tabBarActiveTintColor ?? colors.primary
  const inactiveTintColor: ColorValue =
    focusedOptions?.tabBarInactiveTintColor ?? colors.muted
  const tabBarStyle = {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background,
    height: 72 + insets.bottom,
    paddingTop: 8,
    paddingBottom: insets.bottom,
    paddingHorizontal: 4,
  }

  return (
    <View style={tabBarStyle}>
      {state.routes.map((route) => {
        const routeIndex = state.routes.findIndex(
          (stateRoute) => stateRoute.key === route.key,
        )
        const isFocused = state.index === routeIndex
        const options = descriptors[route.key]?.options

        const onPress = () => {
          const event = navigation.emit({
            type: 'tabPress',
            target: route.key,
            canPreventDefault: true,
          })

          if (!isFocused && !event?.defaultPrevented) {
            navigation.navigate(route.name, route.params)
          }
        }

        const onLongPress = () => {
          navigation.emit({
            type: 'tabLongPress',
            target: route.key,
          })
        }

        const icon = options?.tabBarIcon?.({
          focused: isFocused,
          color: isFocused ? activeTintColor : inactiveTintColor,
          size: 24,
        })
        const itemColor = isFocused ? activeTintColor : inactiveTintColor
        // react-navigation's tabBarLabel render prop is typed to take a
        // plain string color (unlike tabBarIcon's ColorValue) — coerce here,
        // at the one place that actually needs it.
        const label = getLabel(options?.tabBarLabel, {
          isFocused,
          color: String(itemColor),
        })

        return (
          <Pressable
            key={route.key}
            accessibilityRole="tab"
            accessibilityState={{ selected: isFocused }}
            accessibilityLabel={options?.tabBarAccessibilityLabel}
            testID={options?.tabBarButtonTestID}
            onPress={onPress}
            onLongPress={onLongPress}
            style={{
              flex: 1,
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <View
              style={{
                height: 24,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </View>
            {label && options?.tabBarShowLabel !== false ? (
              <Text
                numberOfLines={1}
                className="mt-1 text-xs font-medium"
                style={{ color: itemColor }}
              >
                {label}
              </Text>
            ) : null}
          </Pressable>
        )
      })}
    </View>
  )
}
